import * as React from 'react';
import ClassIcon from '@material-ui/icons/Class';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { DeepReadonly } from 'utility-types';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { NavLinkListItem } from './NavLinkListItem';
import { PECEnv } from '@pec/aion-ui-core/interfaces/serverTokens';
import { SectionContainer } from '../../containers/Nav/Section';
import { TrainingManagementMendixId } from '@pec/aion-ui-core/enums/MendixPageIds';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { InlineBadge } from '../InlineBadge';
import { fetchPendingCoursesAndProgramsCount } from '@pec/aion-ui-core/actions/fetchPendingCoursesAndProgramsCount';

const { AionTrainingComplianceUI } = AppName;

export type Props = {
  organization: DeepReadonly<IOrganization>;
};

export const TrainingSection: React.FC<Props> = ({ organization }) => {
  const { TrainingCompliance } = OrganizationFeature;
  const { id, features } = organization;
  const { t } = useTranslation();
  const { serverTokens } = useSelector((state: CommonRootState) => state.serverTokens);
  const { userInfo } = useSelector((state: CommonRootState) => state.userInfo);
  const { pendingCoursesAndProgramsCount } = useSelector((state: CommonRootState) => state.notifications);
  const [doceboUrl, setDoceboUrl] = useState('');
  const dispatch = useDispatch();
  const { featureFlags } = useSelector((state: CommonRootState) => state.featureFlags);

  useEffect(() => {
    const { beta, dev, production } = PECEnv;
    const doceboUrlMap = {
      [dev]: 'https://veriforce-lmssandbox.docebosaas.com/learn/mycourses?mclp0=s-_"not_started"_"in_progress"',
      [beta]: 'https://veriforcesandbox.docebosaas.com/learn/mycourses?mclp0=s-_"not_started"_"in_progress"',
      [production]: 'https://veriforce-lms.docebosaas.com/learn/mycourses?mclp0=s-_"not_started"_"in_progress"'
    };
    if (serverTokens) {
      setDoceboUrl(doceboUrlMap[serverTokens.pecEnv] || doceboUrlMap[dev]);
    }
  }, [serverTokens]);

  useEffect(() => {
    if (featureFlags?.length) {
      let feature = featureFlags.find(x => x.Key === 'FeatureManagement:AionAccessLMSUI');
      let isEnabled = atob(feature?.Value || '');
      if (userInfo?.userId && isEnabled === 'true') {
        dispatch(fetchPendingCoursesAndProgramsCount(userInfo.userId));
      }
    }
  }, [dispatch, featureFlags, userInfo]);

  return (
    <SectionContainer
      id={AionTrainingComplianceUI}
      subheader={t('components.trainingSection.training', 'Training')}
      icon={<ClassIcon />}
      hasNotifications={Boolean(pendingCoursesAndProgramsCount)}
    >
      {features.includes(TrainingCompliance) &&
        (process.env.REACT_APP_NAME === AionTrainingComplianceUI ? (
          <>
            <NavLinkListItem inset to={`/${id}/training-compliance`}>
              {t('components.trainingSection.trainingCompliance', 'Training Compliance')}
            </NavLinkListItem>
            {features.includes(OrganizationFeature.Client) && (
              <NavLinkListItem inset to={`/${id}/training-compliance/assigned-employees-report`}>
                {t('components.trainingSection.assignedEmployeesReport', 'Assigned Employees Report')}
              </NavLinkListItem>
            )}
          </>
        ) : (
          <>
            <AnchorListItem inset href={`/organizations/${id}/training-compliance`}>
              {t('components.trainingSection.trainingCompliance', 'Training Compliance')}
            </AnchorListItem>
            {features.includes(OrganizationFeature.Client) && (
              <AnchorListItem inset href={`/organizations/${id}/training-compliance/assigned-employees-report`}>
                {t('components.trainingSection.assignedEmployeesReport', 'Assigned Employees Report')}
              </AnchorListItem>
            )}
          </>
        ))}
      {features.includes(OrganizationFeature.VerisourceTMNavigation) && (
        <FeatureFlagWrapper>
          <Feature
            name={'HideLinkVeriforceCompany'}
            render={(isEnabled: boolean) => (
              <AnchorListItem
                inset
                href={ isEnabled ? undefined : `/veriforce-integration/${id}?RedirectUrl=/VS/start.aspx?mx=${TrainingManagementMendixId.TrainingManagement}`}
              >
                {t('components.trainingSection.trainingManagement', 'Training Management')}
              </AnchorListItem>
            )}
          />
        </FeatureFlagWrapper>
      )}
      <AnchorListItem inset href="/TRKR/Default.aspx">
        {t('components.trainingSection.trainingTracker', 'Training Tracker')}
      </AnchorListItem>
      {features.includes(OrganizationFeature.VeriforceLearnAccess) && (
        <FeatureFlagWrapper>
          <Feature name="AionAccessLMSUI">
            <AnchorListItem target="_blank" inset href={doceboUrl}>
              <InlineBadge badgeContent={pendingCoursesAndProgramsCount}>
                {t('components.trainingSection.veriforceLearn', 'Veriforce Learn')}
              </InlineBadge>
            </AnchorListItem>
          </Feature>
        </FeatureFlagWrapper>
      )}
    </SectionContainer>
  );
};
